import { Accordion, Button, Form, Table } from "react-bootstrap";
import { Permission, PermissionAction, User, UserGroup } from "../../../../models/User";
import {
    useAddUserToGroupMutation,
    useGetAllMediaQuery,
    useGetProjectsQuery,
    useGetUserGroupPermissionsQuery,
    useGetUsersQuery,
    useRemoveUserFromGroupMutation,
} from "../../../../store/api/kinesense";
import { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../../store";
import { capitaliseFirst } from "../../../../utilities/helpers";

interface UserGroupDetailsProps {
    group: UserGroup;
}

export function UserGroupDetails(props: UserGroupDetailsProps) {
    const user = useSelector((state: ApplicationState) => state.user);
    const userCanViewOtherUsers =
        User.can(user.permissions.permissions, "read", "users") ||
        User.can(user.permissions.permissions, "peek", "users");
    const userCanUpdateGroupUsers =
        !props.group.isAdmin && !props.group.isBasic && User.can(user.permissions.permissions, "update", "groups");

    const { data: permissions } = useGetUserGroupPermissionsQuery({ groupId: props.group.groupId });
    const { data: projects } = useGetProjectsQuery();
    const { data: users } = useGetUsersQuery({}, { skip: !userCanViewOtherUsers });

    const [groupUsers, setGroupUsers] = useState<User[]>([]);
    const [showAddUserSelect, setShowAddUserSelect] = useState(false);
    const [removedUsers, setRemovedUsers] = useState<User[]>([]);

    const possibleNewUsers = useMemo(() => {
        if (!users || !groupUsers) {
            return [];
        }

        return users
            .filter((user) => !user.groupIds.includes(props.group.groupId))
            .map((user) => ({
                value: user.username,
                label: user.username,
            }));
    }, [groupUsers]);

    const [requestAddUserGroup] = useAddUserToGroupMutation();
    const [removeUserFromGroup] = useRemoveUserFromGroupMutation();

    const actionInfoText = {
        peek: "Allows a user to know a resource exists and its name, but not further details",
    };

    useEffect(() => {
        if (users) {
            setGroupUsers(users.filter((user) => user.groupIds.includes(props.group.groupId)));
        }
    }, [users, props.group.groupId]);

    const createResourceString = (permission: Permission) => {
        if (permission.condition === undefined) {
            return "All";
        }

        switch (permission.subject) {
            case "projects":
                return projects
                    ? projects.find((p) => p.projectId == permission.condition.projectId)?.name
                    : "Unknown Project";
            default:
                return `Unknown`;
        }
    };

    const handleRemoveUser = (user: User) => {
        if (!userCanUpdateGroupUsers) {
            return;
        }

        removeUserFromGroup({ groupId: props.group.groupId, username: user.username });
    };

    const handleNewUserSelected = (selectedUser: { value: string; label: string }) => {
        if (!userCanUpdateGroupUsers) {
            return;
        }

        if (selectedUser) {
            const newUser = users?.find((user) => user.username === selectedUser.value);

            if (newUser) {
                newUser.groupIds.push(props.group.groupId);
                setGroupUsers((prev) => [...prev, newUser]);
                setShowAddUserSelect(false);
                requestAddUserGroup({ groupId: props.group.groupId, username: newUser.username });
            }
        }
    };

    return (
        <div>
            {props.group.description != undefined && props.group.description?.trim() != "" && (
                <div className="mb-3">
                    <h5>Description</h5>
                    <div>{props.group.description}</div>
                </div>
            )}

            <h5>Permissions</h5>

            {permissions && permissions.length === 0 && (
                <div>
                    <span className="text-muted">No group permissions set</span>
                </div>
            )}

            {permissions && permissions.length > 0 && (
                <Table>
                    <thead>
                        <tr>
                            <th style={{ width: "15%" }}>Permission</th>
                            <th
                                style={{
                                    width: "10%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Resource
                            </th>
                            {(["peek", "read", "manage", "create", "update", "delete"] as PermissionAction[]).map(
                                (action) => (
                                    <th
                                        key={action}
                                        style={{ width: "10%", textAlign: "center" }}
                                        title={actionInfoText[action]}
                                    >
                                        {capitaliseFirst(action)}
                                    </th>
                                ),
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {permissions
                            .toSorted((a, b) => a.subject.localeCompare(b.subject))
                            .map((permission) => (
                                <tr key={permission.permissionId}>
                                    <td>{capitaliseFirst(permission.subject)}</td>
                                    <td
                                        style={{
                                            width: "10%",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {createResourceString(permission)}{" "}
                                    </td>

                                    {(
                                        ["peek", "read", "manage", "create", "update", "delete"] as PermissionAction[]
                                    ).map((action) => (
                                        <td key={action} style={{ textAlign: "center" }}>
                                            <Form.Check
                                                type="checkbox"
                                                disabled={true}
                                                readOnly
                                                checked={permission.actions.includes(action)}
                                            />
                                        </td>
                                    ))}
                                </tr>
                            ))}
                    </tbody>
                </Table>
            )}

            <h5>Users</h5>

            {groupUsers && groupUsers.length === 0 && (
                <div>
                    <span className="text-muted">No users in this group</span>
                </div>
            )}

            {groupUsers && groupUsers.length > 0 && (
                <Table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th style={{ width: "15%" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {groupUsers.map((user) => (
                            <tr key={user.username}>
                                <td>{user.username}</td>

                                <td>
                                    {userCanUpdateGroupUsers && (
                                        <Button variant="link" size="sm" onClick={() => handleRemoveUser(user)}>
                                            Remove
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {possibleNewUsers && possibleNewUsers.length > 0 && (
                <div>
                    {userCanUpdateGroupUsers && !showAddUserSelect && (
                        <Button variant="link" size="sm" onClick={() => setShowAddUserSelect(true)}>
                            Add User
                        </Button>
                    )}
                    {showAddUserSelect && (
                        <Select
                            options={possibleNewUsers}
                            placeholder="Add user..."
                            classNamePrefix="react-select"
                            onChange={handleNewUserSelected}
                        />
                    )}
                </div>
            )}
        </div>
    );
}
